import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  Sparkles, ShieldAlert, Landmark, Pickaxe, Leaf, Waves, ChevronRight, Filter,
} from "lucide-react";

/**
 * Cross-Pillar Executive Intelligence — presentation-only advisory feed that
 * SURFACES relationships between existing pillars (Land · Mining · Survey ·
 * Environment) using the current APIs. Never duplicates records, never
 * transfers ownership, never modifies source data.
 */

const useAdvisories = () => {
  const marketplace = useQuery({ queryKey: ["cp-mkt"],  queryFn: async () => (await api.get("/cadastre360/sovereign/marketplace")).data });
  const risks       = useQuery({ queryKey: ["cp-risk"], queryFn: async () => (await api.get("/cadastre360/gsros/national-risk-register")).data });
  const pas         = useQuery({ queryKey: ["cp-pa"],   queryFn: async () => (await api.get("/cadastre360/environment/protected-areas")).data });
  const permits     = useQuery({ queryKey: ["cp-perm"], queryFn: async () => (await api.get("/cadastre360/environment/permits")).data });
  const incidents   = useQuery({ queryKey: ["cp-inc"],  queryFn: async () => (await api.get("/cadastre360/environment/incidents")).data });

  const rows = [];

  // Mining ↔ Environment
  (marketplace.data?.mining_opportunities || []).slice(0, 6).forEach((m) => {
    (pas.data || []).slice(0, 3).forEach((pa) => {
      if (m.province === pa.province) {
        rows.push({
          category: "mining_intersects_pa",
          severity: "high",
          fromPillar: "Mining",
          toPillar: "Environmental",
          title: `${m.commodity || "Mineral right"} activity in ${m.province}`,
          detail: `Potential intersection with ${pa.designation} (${pa.name}). Advisory raised for Cabinet Command Centre.`,
          links: [{ label: "Open Mining Cadastre", to: "/app/mining" },
                    { label: "Open Environmental Cadastre", to: "/app/environment" }],
        });
      }
    });
  });

  // Land ↔ Environment
  (marketplace.data?.land_opportunities || []).slice(0, 6).forEach((l) => {
    (pas.data || []).slice(0, 3).forEach((pa) => {
      if (l.province === pa.province) {
        rows.push({
          category: "parcel_overlaps_protected",
          severity: "medium",
          fromPillar: "Land",
          toPillar: "Environmental",
          title: `Parcel ${l.parcel_number} in ${l.province}`,
          detail: `Potential overlap with ${pa.name} buffer (${pa.buffer_km} km). Development requires EIA review.`,
          links: [{ label: "Open Land Cadastre", to: "/app/land" },
                    { label: "Open Environmental Cadastre", to: "/app/environment" }],
        });
      }
    });
  });

  // Incidents ↔ Downstream Mining
  (incidents.data || []).slice(0, 6).forEach((inc) => {
    rows.push({
      category: "env_incident_impacts_mining",
      severity: inc.severity || "medium",
      fromPillar: "Environmental",
      toPillar: "Mining",
      title: `${inc.incident_type} · ${inc.province}`,
      detail: `${inc.location_description}. Downstream mining/water rights may be affected. Community impact: ${inc.community_impact || "n/a"}.`,
      links: [{ label: "Open Incidents", to: "/app/environment" },
                { label: "Open Cabinet Command", to: "/app/gsros" }],
    });
  });

  // Permits ↔ Compliance
  (permits.data || []).slice(0, 4).forEach((p) => {
    if (p.status === "active") {
      rows.push({
        category: "permit_active_advisory",
        severity: "low",
        fromPillar: "Environmental",
        toPillar: "Compliance",
        title: `Permit ${p.reference} active`,
        detail: `${p.permit_type} held by ${p.holder} · expires ${p.expiry_date}. Inspection cadence recommended.`,
        links: [{ label: "Open Compliance", to: "/app/compliance" }],
      });
    }
  });

  // Risks ↔ Provinces
  (risks.data?.risks || []).filter((r) => r.impact === "high" || r.impact === "critical").slice(0, 6).forEach((r) => {
    rows.push({
      category: "national_risk_advisory",
      severity: r.impact === "critical" ? "high" : "medium",
      fromPillar: "GSROS",
      toPillar: r.category,
      title: `${r.id} · ${r.risk}`,
      detail: `Impact ${r.impact} · owner ${r.owner}. Escalated to executive attention.`,
      links: [{ label: "Open GSROS Risk Register", to: "/app/gsros" }],
    });
  });

  return { rows, loading: marketplace.isLoading || pas.isLoading || incidents.isLoading };
};

const sevColour = (s) => s === "high" || s === "critical" ? "#E67E7E" : s === "medium" ? "#C99A2E" : "#4FA3D1";
const pillarIcon = (p) => {
  if (p === "Land") return Landmark;
  if (p === "Mining") return Pickaxe;
  if (p === "Environmental") return Leaf;
  if (p === "Compliance") return ShieldAlert;
  return Sparkles;
};

export const CrossPillarIntelligence = () => {
  const { rows, loading } = useAdvisories();
  const [filter, setFilter] = useState("all");
  const filtered = filter === "all" ? rows : rows.filter((r) => r.severity === filter);

  return (
    <div className="p-6 space-y-4" data-testid="page-cross-pillar">
      <div className="rounded-sm border p-6 flex items-start gap-4"
           style={{ borderColor: "#7B2FBE60",
                     background: "linear-gradient(120deg, rgba(7,27,43,0.95), rgba(11,53,88,0.85), rgba(123,47,190,0.25))" }}>
        <Sparkles size={32} className="mt-1 text-[#C99A2E]" />
        <div>
          <div className="font-mono uppercase tracking-[0.32em] text-[10px] text-[#C99A2E]">Cross-Pillar Executive Intelligence</div>
          <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1">Read-only advisories across the four sovereign pillars</h1>
          <div className="text-[13px] text-[#DCE3E8] mt-2 max-w-3xl">
            Surfaces relationships between Land, Mining, Survey and Environmental Governance
            using existing APIs. Nothing here duplicates or modifies source records — each
            advisory deep-links to the pillar that owns the data.
          </div>
        </div>
      </div>

      <div className="rounded-sm border border-white/10 p-3 flex flex-wrap items-center gap-2" style={{ background: "rgba(11,53,88,0.35)" }}>
        <Filter size={12} className="text-[#7C93A5]" />
        {["all", "high", "medium", "low"].map((s) => (
          <button key={s} onClick={() => setFilter(s)}
                  data-testid={"cp-filter-" + s}
                  className="rounded-sm px-2 py-1 text-[10px] font-mono uppercase tracking-[0.14em] border"
                  style={{
                    background: s === filter ? sevColour(s === "all" ? "medium" : s) + "30" : "transparent",
                    color: s === filter ? "#F6F8FA" : "#B3C2CC",
                    borderColor: s === filter ? sevColour(s === "all" ? "medium" : s) : "rgba(255,255,255,0.08)",
                  }}>
            {s === "all" ? "All" : s}
          </button>
        ))}
        <div className="ml-auto font-mono text-[10px] text-[#7C93A5]">
          {loading ? "Loading…" : `${filtered.length} advisories`}
        </div>
      </div>

      <div className="rounded-sm border border-white/10" style={{ background: "rgba(11,53,88,0.25)" }} data-testid="cp-advisory-feed">
        {filtered.map((r, i) => {
          const From = pillarIcon(r.fromPillar);
          const To = pillarIcon(r.toPillar);
          const col = sevColour(r.severity);
          return (
            <div key={r.category + "-" + i} className="flex items-start gap-3 px-3 py-2.5 border-b border-white/5">
              <div className="flex items-center gap-1 mt-1">
                <From size={13} style={{ color: "#C99A2E" }} />
                <ChevronRight size={11} className="text-[#7C93A5]" />
                <To size={13} style={{ color: "#2E7D6B" }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="rounded-sm px-1.5 py-0.5 text-[10px] font-mono uppercase"
                        style={{ background: col + "25", color: col }}>{r.severity}</span>
                  <span className="text-[10.5px] font-mono uppercase tracking-[0.14em] text-[#7C93A5]">
                    {r.fromPillar} → {r.toPillar}
                  </span>
                </div>
                <div className="text-[13px] text-[#F6F8FA] font-medium mt-0.5">{r.title}</div>
                <div className="text-[11.5px] text-[#DCE3E8] mt-0.5 leading-relaxed">{r.detail}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {(r.links || []).map((l) => (
                    <Link key={l.to + l.label} to={l.to}
                          className="rounded-sm px-2 py-0.5 border border-white/10 text-[10px] font-mono uppercase text-[#F6F8FA] inline-flex items-center gap-1">
                      {l.label} <ChevronRight size={9} />
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
        {!loading && filtered.length === 0 && (
          <div className="p-6 text-[11.5px] text-[#7C93A5] italic">No advisories for this filter.</div>
        )}
      </div>
    </div>
  );
};

export default CrossPillarIntelligence;
